// ---------------------------------------------------------------------------
// CLOCK-IN ROUTE ACCESS
//
// A clocked-out employee can reach exactly one screen: Check In / Out.
//
// This used to be enforced by REWRITING userInfo.routes inside Header's
// componentDidUpdate, once the /account/getUsers response came back. That had
// two problems, and typing a URL straight into the address bar hit both:
//
//   1. It was asynchronous. On a fresh page load the route list rehydrates
//      from redux-persist with the FULL granted set, so every protected route
//      mounted and the whole sidebar rendered until the round-trip landed.
//
//   2. Two sagas write userInfo.routes and only one of them knows about
//      shifts. refreshUserInfoSaga (fired from Base.componentDidMount)
//      overwrites routes with the granted list from /account/getUser, because
//      that is what "routes" means there — what you are ALLOWED, not what you
//      can reach right now. So it raced Header's reduction and frequently won,
//      putting the full menu back.
//
// The fix is to stop conflating the two. userInfo.routes stays the granted
// set; the shift gate is applied here, at render time, from userInfo
// .isClockedIn — a value the server recomputes from ClockRecord on every
// getUser (see AccountService.getUserInfo). No round-trip, no race, and the
// same answer on first paint as on the tenth re-render.
//
// This is presentation only. The server refuses shift-bound writes on its own
// (ClockService.openShiftFor) and remains the authority — the point of doing
// it here is that the employee never reaches a screen that cannot work.
// ---------------------------------------------------------------------------

export const CHECK_IN_OUT = 'check-in-out';

/**
 * Clock in / out is a DESKTOP-ONLY action.
 *
 * Employees punch in at the in-store station, not from a phone, so the screen
 * is withheld on mobile in both directions — a clocked-out employee cannot
 * clock in from their phone, and a clocked-in one cannot clock out from it
 * either. MenuItems already leaves 'check-in-out' out of both mobile
 * allow-lists; this applies the same rule to the ROUTER, so the screen can't
 * be reached by URL on a phone the way /ticket-out could.
 *
 * Same detection as Base and Header (viewport, iOS Safari, or a touch/mobile
 * user agent) — deliberately identical so the three cannot classify the same
 * device differently and show a menu that routes nowhere.
 */
export function isMobileViewport() {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return false;
  }
  const isIOSSafari = /iP(ad|hone|od).*Safari/i.test(navigator.userAgent);
  const isMobileUA =
    /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|Mobile/i.test(
      navigator.userAgent
    );
  const isTouchOnly =
    window.matchMedia('(pointer: coarse)').matches &&
    navigator.maxTouchPoints > 0;

  return (
    window.matchMedia('(max-width: 800px)').matches ||
    isIOSSafari ||
    isMobileUA ||
    isTouchOnly
  );
}

/**
 * What a clocked-out employee can still reach.
 *
 * Desktop: Check In / Out, so they can start a shift. Mobile: nothing — the
 * clock lives on the desktop station, so there is no screen on a phone that
 * can help them.
 *
 * Callers must still apply the CHIP gate (see clockedOutRoutesFor) — this
 * answers "what does this device offer", not "has this person identified
 * themselves at a station yet".
 */
export function clockedOutRoutes() {
  return isMobileViewport() ? [] : [CHECK_IN_OUT];
}

/**
 * The same, but gated on the employee having tapped their RFID chip.
 *
 * `sessionId` is stamped by processRFID on a chip tap and cleared on clock-out
 * / logout, so it is the app's "this person is present at a station" signal.
 * Until it exists the employee gets NO routes, and Base renders CheckIn404 —
 * "You're currently not clocked in. Please scan your Chip to check in."
 *
 * The order matters and is intentional: log in → scan chip → clock in. Handing
 * out the clock screen before the chip step lets someone reach it without the
 * system having identified them at a terminal, which is the whole point of the
 * tap. An earlier version of this file returned the clock unconditionally and
 * collapsed that flow to a single step.
 */
export function clockedOutRoutesFor(userInfo) {
  if (!userInfo || !userInfo.sessionId) return [];
  return clockedOutRoutes();
}

/**
 * Roles that never punch the customer-facing clock.
 *
 * Managers, master admins and tech admins carry ROLE_ADMIN and the app never
 * opens a shift for them. Reading Guy and Security are back-office: the
 * heuristic is that anyone without ROLE_CUSTOMER (the dashboard customer
 * access role) is not on the shift clock and keeps their full route set.
 *
 * Accepts either shape of authority list the app passes around: userInfo
 * carries `authority` as plain strings, the employees list carries
 * `authorities` as objects.
 */
export function isClockInExempt(user) {
  if (!user) return false;

  const raw = user.authorities || user.authority;
  if (!Array.isArray(raw)) return false;

  const auths = raw.map((a) => (typeof a === 'string' ? a : a && a.authority));

  if (auths.includes('ROLE_ADMIN') || auths.includes('ROLE_MASTER_ADMIN')) {
    return true;
  }
  return !auths.includes('ROLE_CUSTOMER');
}

/**
 * True when this user is on the shift clock and currently clocked OUT.
 *
 * Deliberately `=== false`: `undefined` means the server has not told us yet
 * (an older cached userInfo, or a response that predates the field), and
 * locking someone out of the app on a missing value would be worse than the
 * bug this closes. Absent evidence, let them through — the server still
 * refuses the write.
 */
export function isClockedOut(userInfo) {
  if (!userInfo) return false;
  if (isClockInExempt(userInfo)) return false;
  return userInfo.isClockedIn === false;
}

/**
 * The routes this user can actually reach right now.
 *
 * Use this everywhere a route list is consumed — the mounted <Route> set and
 * the sidebar both — so the menu and the router can never disagree about
 * where someone is allowed to be.
 */
export function allowedRoutes(userInfo) {
  return routesForShiftState(userInfo);
}

/**
 * What an employee who is clocked in but NOT yet online keeps.
 *
 * "Online" is stamped by the RFID tap. Between clocking in and tapping, the
 * employee has a shift but no station, so the screens that record work against
 * that station are withheld and only the read-ish ones remain.
 */
export const OFFLINE_CLOCKED_IN_ROUTES = [
  'dashboard',
  'customers',
  'raffle',
  'role-privilege',
];

/**
 * THE shift-state → routes decision, for both the logged-in user and any
 * employee record in the switcher list.
 *
 * Single implementation on purpose. This logic used to live inline in Header
 * and was applied by REWRITING userInfo.routes after /account/getUsers came
 * back — which raced refreshUserInfoSaga, whose job is to write the GRANTED
 * route list from /account/getUser. Whichever landed last won, so a clocked-out
 * or not-yet-online employee could get the full menu handed back to them.
 *
 * Applying it here, at render time, from the three server-authoritative fields
 * (isClockedIn recomputed from ClockRecord, plus isUserOnline and sessionId)
 * means there is nothing to race: routes stays "what you are granted" and this
 * answers "what you can reach right now", the same on first paint as on the
 * tenth re-render.
 */
export function routesForShiftState(user) {
  const granted = user && user.routes ? [...user.routes] : [];
  const mobile = isMobileViewport();

  // On mobile the clock is never offered, in either direction.
  const withoutClock = () => granted.filter((id) => id !== CHECK_IN_OUT);

  // Back-office roles never punch the clock — no shift state to apply.
  if (isClockInExempt(user)) return granted;

  // Unknown clock state: an older cached userInfo, or a backend response that
  // predates the field. Gating someone out of the app on a missing value would
  // be worse than the drift this closes, so let them through — the server
  // still refuses every shift-bound write on its own.
  const clockedIn = user ? user.isClockedIn : undefined;
  if (clockedIn !== true && clockedIn !== false) {
    return mobile ? withoutClock() : granted;
  }

  if (!clockedIn) {
    // Clocked out: the clock screen, and only once the chip has been tapped.
    // clockedOutRoutesFor already returns [] on mobile.
    return clockedOutRoutesFor(user);
  }

  // Clocked in. The online/offline split is deliberately NOT applied on
  // mobile — phones have no RFID reader, so gating on the tap would strand
  // an employee who is legitimately on shift.
  if (mobile) return withoutClock();

  return user.isUserOnline
    ? granted
    : granted.filter((id) => OFFLINE_CLOCKED_IN_ROUTES.includes(id));
}
